import React from 'react';
import { Card, CardContent, CardHeader, Typography, Box } from '@mui/material';

const FoundationDiagram = ({ results }) => {
  const { foundation_geometry, equilibrium, input_parameters = { loads: {} } } = results;

  if (!foundation_geometry || !equilibrium || !equilibrium.pile_loads) {
    return (
      <Card>
        <CardHeader title="Foundation Diagram" />
        <CardContent>
          <Typography variant="body2" color="text.secondary">
            Diagram not available - equilibrium analysis data missing
          </Typography>
        </CardContent>
      </Card>
    );
  }

  const { dimensions = {}, pile_layout = [], num_piles = 0 } = foundation_geometry;
  const loads = input_parameters.loads || {};
  const pile_loads = equilibrium.pile_loads || [];
  const neutral_axis = equilibrium.neutral_axis || {};
  const pivot_point = equilibrium.pivot_point || { x: 0, y: 0 };
  const L = dimensions.L || 2.0;
  const W = dimensions.W || 2.0;
  const H = dimensions.H || 0.8;
  const { x_rot, y_rot } = neutral_axis;
  const halfSpanX = (W || 2) * 0.55;
  const halfSpanY = (L || 2) * 0.55;
  const showNaHoriz = y_rot != null && Number.isFinite(Number(y_rot)) && Math.abs(Number(y_rot)) <= halfSpanY * 1.2;
  const showNaVert = x_rot != null && Number.isFinite(Number(x_rot)) && Math.abs(Number(x_rot)) <= halfSpanX * 1.2;
  const showFulcrum =
    pivot_point &&
    Number.isFinite(Number(pivot_point.x)) &&
    Number.isFinite(Number(pivot_point.y)) &&
    Math.abs(Number(pivot_point.x)) <= halfSpanX * 1.1 &&
    Math.abs(Number(pivot_point.y)) <= halfSpanY * 1.1;

  // Scale factors for visualization
  const scale = 100; // pixels per meter
  const svgWidth = W * scale + 100;
  const svgHeight = L * scale + 100;

  // Center the foundation
  const offsetX = 50;
  const offsetY = 50;

  return (
    <Card>
      <CardHeader title="Foundation Geometry & Load Distribution" />
      <CardContent>
        <Typography variant="body2" color="text.secondary" gutterBottom>
          Top view showing pile layout and loads. Blue arrows show compression, red arrows show tension.
        </Typography>

        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <svg width={svgWidth} height={svgHeight} style={{ border: '1px solid #ccc' }}>
            {/* Coordinate axes (global plan coordinates) */}
            <line
              x1={offsetX + W * scale / 2}
              y1={offsetY + L * scale / 2}
              x2={offsetX + W * scale / 2 + 36}
              y2={offsetY + L * scale / 2}
              stroke="#2e7d32"
              strokeWidth="2"
              markerEnd="url(#axisArrow)"
            />
            <text
              x={offsetX + W * scale / 2 + 42}
              y={offsetY + L * scale / 2 + 4}
              fontSize="10"
              fill="#2e7d32"
              fontWeight="bold"
            >
              +X
            </text>
            <line
              x1={offsetX + W * scale / 2}
              y1={offsetY + L * scale / 2}
              x2={offsetX + W * scale / 2}
              y2={offsetY + L * scale / 2 - 36}
              stroke="#6a1b9a"
              strokeWidth="2"
              markerEnd="url(#axisArrow)"
            />
            <text
              x={offsetX + W * scale / 2 + 4}
              y={offsetY + L * scale / 2 - 42}
              fontSize="10"
              fill="#6a1b9a"
              fontWeight="bold"
            >
              +Y
            </text>
            {/* Foundation outline */}
            <rect
              x={offsetX}
              y={offsetY}
              width={W * scale}
              height={L * scale}
              fill="none"
              stroke="#666"
              strokeWidth="2"
            />

            {/* Foundation label */}
            <text
              x={offsetX + W * scale / 2}
              y={offsetY - 10}
              textAnchor="middle"
              fontSize="12"
              fill="#666"
            >
              {(W || 0).toFixed(1)}m × {(L || 0).toFixed(1)}m Foundation
            </text>

            {input_parameters?.groundwater_depth != null && Number(input_parameters.groundwater_depth) > 0 && (
              <g>
                <line
                  x1={offsetX}
                  y1={offsetY - 22}
                  x2={offsetX + W * scale}
                  y2={offsetY - 22}
                  stroke="#0288d1"
                  strokeWidth="1.5"
                  strokeDasharray="6 4"
                  opacity={0.95}
                />
                <text
                  x={offsetX + (W * scale) / 2}
                  y={offsetY - 26}
                  textAnchor="middle"
                  fontSize="9"
                  fill="#0288d1"
                  fontWeight="bold"
                >
                  Ground water level
                </text>
              </g>
            )}

            {/* Pile layout */}
            {pile_layout.map((pile, index) => {
              const [x, y] = pile; // pile coordinates relative to foundation center
              const pileLoad = pile_loads[index] || 0;

              // Convert to SVG coordinates (center of foundation is at L/2, W/2)
              const svgX = offsetX + W * scale / 2 + x * scale;
              const svgY = offsetY + L * scale / 2 + y * scale;

              // Pile marker
              const isTension = pileLoad < 0;
              const loadMagnitude = Math.abs(pileLoad);
              const arrowLength = Math.min(loadMagnitude / 10, 30); // Scale arrow length

              return (
                <g key={index}>
                  {/* Pile position marker */}
                  <circle
                    cx={svgX}
                    cy={svgY}
                    r="4"
                    fill={isTension ? "#f44336" : "#2196f3"}
                  />

                  {/* Pile load arrow */}
                  {loadMagnitude > 1 && (
                    <>
                      {/* Arrow line */}
                      <line
                        x1={svgX}
                        y1={svgY}
                        x2={svgX}
                        y2={svgY - arrowLength}
                        stroke={isTension ? "#f44336" : "#2196f3"}
                        strokeWidth="2"
                        markerEnd="url(#arrowhead)"
                      />

                      {/* Arrow label */}
                      <text
                        x={svgX + 8}
                        y={svgY - arrowLength - 5}
                        fontSize="10"
                        fill={isTension ? "#f44336" : "#2196f3"}
                      >
                        {(loadMagnitude || 0).toFixed(0)}kN
                      </text>
                    </>
                  )}

                  {/* Pile number */}
                  <text
                    x={svgX + 8}
                    y={svgY + 15}
                    fontSize="10"
                    fill="#666"
                  >
                    P{index + 1}
                  </text>
                </g>
              );
            })}

            {/* Neutral axis (w=0): only if solver rotation gives a locus inside the cap (avoids km-scale artifacts when θ≈0) */}
            {showNaHoriz && (
              <line
                x1={offsetX}
                y1={offsetY + L * scale / 2 + Number(y_rot) * scale}
                x2={offsetX + W * scale}
                y2={offsetY + L * scale / 2 + Number(y_rot) * scale}
                stroke="#ff9800"
                strokeWidth="2"
                strokeDasharray="5,5"
              >
                <title>Displacement neutral axis (w = 0) in plan</title>
              </line>
            )}
            {showNaVert && (
              <line
                x1={offsetX + W * scale / 2 + Number(x_rot) * scale}
                y1={offsetY}
                x2={offsetX + W * scale / 2 + Number(x_rot) * scale}
                y2={offsetY + L * scale}
                stroke="#ff9800"
                strokeWidth="2"
                strokeDasharray="5,5"
              >
                <title>Displacement neutral axis (w = 0) in plan</title>
              </line>
            )}

            {/* Pivot (stiffness centroid on compression side for overturning) */}
            {showFulcrum && (
              <>
                <circle
                  cx={offsetX + W * scale / 2 + ((loads.My || 0) >= 0 ? pivot_point.x : -pivot_point.x) * scale}
                  cy={offsetY + L * scale / 2 + ((loads.Mx || 0) <= 0 ? -pivot_point.y : pivot_point.y) * scale}
                  r="6"
                  fill="#e91e63"
                  stroke="#fff"
                  strokeWidth="2"
                />
                <text
                  x={offsetX + W * scale / 2 + ((loads.My || 0) >= 0 ? pivot_point.x : -pivot_point.x) * scale + 10}
                  y={offsetY + L * scale / 2 + ((loads.Mx || 0) <= 0 ? -pivot_point.y : pivot_point.y) * scale}
                  fontSize="10"
                  fill="#e91e63"
                  fontWeight="bold"
                >
                  Fulcrum
                </text>
              </>
            )}

            {/* Arrow marker definition */}
            <defs>
              <marker
                id="arrowhead"
                markerWidth="10"
                markerHeight="7"
                refX="9"
                refY="3.5"
                orient="auto"
              >
                <polygon
                  points="0 0, 10 3.5, 0 7"
                  fill="#2196f3"
                />
              </marker>
              <marker
                id="axisArrow"
                markerWidth="8"
                markerHeight="6"
                refX="7"
                refY="3"
                orient="auto"
              >
                <polygon points="0 0, 8 3, 0 6" fill="#455a64" />
              </marker>
            </defs>
          </svg>
        </Box>

        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" color="text.secondary">
            <strong>Legend:</strong> Blue circles/arrows = compression loads, Red circles/arrows = tension loads
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ color: '#2e7d32', fontWeight: 'bold' }}>→</Box> +X axis,{' '}
            <Box component="span" sx={{ color: '#6a1b9a', fontWeight: 'bold' }}>↑</Box> +Y axis
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ color: '#ff9800', fontWeight: 'bold' }}>---</Box> Displacement neutral axis (w = 0){' '}
            {showNaVert && x_rot != null && `x: ${Number(x_rot).toFixed(3)} m `}
            {showNaHoriz && y_rot != null && `y: ${Number(y_rot).toFixed(3)} m`}
            {!showNaHoriz && !showNaVert && '(not shown — tilt too small; locus would lie far outside the cap)'}
          </Typography>
          <Typography variant="body2" color="text.secondary">
            <Box component="span" sx={{ color: '#e91e63', fontWeight: 'bold' }}>●</Box> Stability fulcrum (compression-side stiffness centroid){' '}
            {showFulcrum && `x: ${Number(pivot_point.x).toFixed(3)} m | y: ${Number(pivot_point.y).toFixed(3)} m`}
            {!showFulcrum && '(not shown — value outside cap outline)'}
          </Typography>
          {(input_parameters.groundwater_depth != null && Number(input_parameters.groundwater_depth) > 0) && (
            <Typography variant="body2" color="text.secondary">
              <Box component="span" sx={{ color: '#0288d1', fontWeight: 'bold' }}>—</Box> Ground water level:{' '}
              <strong>{Number(input_parameters.groundwater_depth).toFixed(2)} m</strong> b.g.l. (plan schematic above; soil-pressure sections use depth to scale)
            </Typography>
          )}
          <Typography variant="body2" color="text.secondary">
            Total piles: {num_piles}, Vertical load: {loads.N || loads.Fz || 0} kN
          </Typography>
        </Box>
      </CardContent>
    </Card>
  );
};

export default FoundationDiagram;
